"use client";

import { ChannelFeedRowView } from "@product/app/workspace/channels/[channelId]/channel-feed-row-view";
import {
  ChannelDataSetTableView,
  ChannelHomeActivityView,
  ChannelHomeView,
  ChannelMetaView,
  ChannelPurposeEditButton,
  ChannelPurposeView,
} from "@product/app/workspace/channels/[channelId]/channel-home-view";
import { resolveFeedActor } from "@product/components/app/feed/feed-actor";
import { groupFeedByDay } from "@product/components/app/feed/feed-day-groups";
import { AskComposerView } from "@product/components/app/sessions/ask-composer-view";
import { ComposerSourcesView } from "@product/components/app/sessions/composer-sources-view";
import type { WorkspaceChannelFeedItem } from "@product/lib/mlx-workspace-client";

import type { ShotChannelOverview, ShotFeedItem } from "./channel-overview";

/** The Overview shows this many of the channel's latest items (ChannelHome's RECENT_ACTIVITY). */
const RECENT_ACTIVITY = 6;
const SHOWN_MEMBERS = 4;

const noReaction = async () => {};

/** An item placed in time: its own time, and a session's times, when it happened. */
function feedItemAt(item: ShotFeedItem, at: number): WorkspaceChannelFeedItem {
  if (item.type === "session") {
    return {
      ...item,
      occurredAt: at,
      activity: {
        ...item.activity,
        firstSeenAt: at,
        lastSeenAt: at,
        session: { ...item.activity.session, createdAt: at, updatedAt: at },
      },
    };
  }
  if (item.type === "file") return { ...item, occurredAt: at, file: { ...item.file, createdAt: at } };
  return { ...item, occurredAt: at } as WorkspaceChannelFeedItem;
}

/**
 * A channel's Overview tab as ChannelHome draws it for a member who manages
 * the channel: its purpose with Edit, who is in it and what it keeps, the
 * ask box, the data set table and the recent activity, compact rows by day.
 * Times are the overview's ages from now, so it reads as current whenever
 * it is drawn, in the viewer's own timezone.
 */
export function ShotChannelOverviewView({ overview }: { overview: ShotChannelOverview }) {
  const now = Date.now();
  const membersByUserId = new Map(overview.directory.map((member) => [member.userId, member]));
  const viewsById = new Map(overview.views.map((view) => [view.id, view]));
  const selfIds = new Set([overview.currentUser.id]);
  // ChannelMeta: the members as the directory names them, up to four, the viewer as You.
  const members = overview.memberIds
    .map((id) =>
      resolveFeedActor(
        { id, type: "user", subjectId: null, clientApp: null, interfaceType: null, executedByTwin: false },
        membersByUserId,
        selfIds,
        overview.currentUser,
      ),
    )
    .filter((member) => member.initials !== "?")
    .slice(0, SHOWN_MEMBERS);
  const items = overview.activity.slice(0, RECENT_ACTIVITY).map(({ agoMs, item }) => feedItemAt(item, now - agoMs));
  const days = groupFeedByDay(items, (item) => item.occurredAt, now);
  return (
    <ChannelHomeView
      purpose={<ChannelPurposeView purpose={overview.purpose} action={<ChannelPurposeEditButton hasPurpose={Boolean(overview.purpose.trim())} />} />}
      meta={
        <ChannelMetaView
          channelId={overview.channelId}
          visibility={overview.visibility}
          updatedAt={now - overview.updatedAgoMs}
          memberCount={overview.memberIds.length}
          members={members}
          fileCount={overview.fileCount}
          memoryCount={overview.memoryCount}
        />
      }
      ask={
        <AskComposerView
          label={`Ask about ${overview.channelName}`}
          placeholder={`Ask about ${overview.channelName}…`}
          suggestions={overview.askSuggestions}
          sources={<ComposerSourcesView sources={overview.sources} offerConnect={false} />}
        />
      }
      dataSets={
        <ChannelDataSetTableView
          channelId={overview.channelId}
          groups={overview.dataSets.map(({ agoMs, ...group }) => ({ ...group, updatedAt: now - agoMs }))}
          defaultViewId={overview.defaultViewId}
        />
      }
      activity={
        <ChannelHomeActivityView
          days={days.map((day) => ({
            id: day.id,
            label: day.label,
            items: day.items.map((item, index) => ({
              key: `${item.type}:${item.id}`,
              row: (
                <ChannelFeedRowView
                  item={item}
                  channelId={overview.channelId}
                  currentUser={overview.currentUser}
                  membersByUserId={membersByUserId}
                  viewsById={viewsById}
                  showConnector={index < day.items.length - 1}
                  density="compact"
                  reactions={{ engagement: item.engagement, pending: false, toggle: noReaction }}
                />
              ),
            })),
          }))}
        />
      }
    />
  );
}
